import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, Camera, MapPin, Clock, Home,
  PenLine, FileText, X, Download, Eraser, Check, ClipboardList,
} from 'lucide-react';

const ETAT_OPTIONS = [
  { value: '', label: '—' },
  { value: 'Sain', label: 'Sain / Neuf' },
  { value: 'Bon état', label: 'Bon état' },
  { value: 'Usé', label: 'Usé / Moyen' },
  { value: 'Dégradé', label: 'Dégradé' },
  { value: 'À noter', label: 'À noter (défaut préexistant)' },
];

/**
 * Formulaire « État des lieux avant démarrage chantier ».
 * Sauvegarde immédiate à la création, puis édition en place.
 * Photos avec géolocalisation navigateur, signature client via pad tactile,
 * export PDF avec rapport photo horodaté + géolocalisé + signatures + tampon RR.
 */
export default function EtatLieuxForm({ initialFiche = null, onExit }) {
  const [fiche, setFiche] = useState(initialFiche);
  const [creating, setCreating] = useState(!initialFiche);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  const [meta, setMeta] = useState({
    client_nom: '', adresse: '', contact_sur_place: '', telephone_contact: '',
    date_visite: new Date().toISOString().slice(0, 10),
    heure_visite: new Date().toTimeString().slice(0, 5),
    intervenant_reno: '',
    releve_compteur_eau: '', releve_compteur_elec: '', releve_compteur_gaz: '',
    reserves_globales: '',
  });

  useEffect(() => {
    if (initialFiche) {
      setFiche(initialFiche);
      setMeta({
        client_nom: initialFiche.client_nom || '',
        adresse: initialFiche.adresse || '',
        contact_sur_place: initialFiche.contact_sur_place || '',
        telephone_contact: initialFiche.telephone_contact || '',
        date_visite: initialFiche.date_visite || '',
        heure_visite: initialFiche.heure_visite || '',
        intervenant_reno: initialFiche.intervenant_reno || '',
        releve_compteur_eau: initialFiche.releve_compteur_eau || '',
        releve_compteur_elec: initialFiche.releve_compteur_elec || '',
        releve_compteur_gaz: initialFiche.releve_compteur_gaz || '',
        reserves_globales: initialFiche.reserves_globales || '',
      });
      setCreating(false);
    }
  }, [initialFiche]);

  const createFiche = async () => {
    if (!meta.client_nom.trim() || !meta.adresse.trim()) {
      toast.error('Client et adresse requis');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...meta, type_fiche: 'etat_lieux', pieces: [] };
      const r = await api.post('/fiches-visite', payload);
      setFiche(r.data.fiche);
      setCreating(false);
      toast.success('État des lieux créé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec création');
    }
    setSaving(false);
  };

  const reload = useCallback(async () => {
    if (!fiche?.fiche_id) return;
    try {
      const r = await api.get(`/fiches-visite/${fiche.fiche_id}`);
      setFiche(r.data.fiche);
    } catch (_) {}
  }, [fiche?.fiche_id]);

  const saveMeta = async () => {
    if (!fiche?.fiche_id) return;
    setSaving(true);
    try {
      // Backend has no PATCH endpoint for meta — reuse create-like: send full payload as new fiche? No.
      // We'll use the existing update endpoint if any. Fall back: patch via signature endpoint won't help.
      // Simple: rely on piece endpoints + a dedicated meta update using existing "PUT /fiches-visite/{id}".
      await api.put(`/fiches-visite/${fiche.fiche_id}`, { ...fiche, ...meta });
      toast.success('Enregistré');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec sauvegarde');
    }
    setSaving(false);
  };

  const addPiece = async () => {
    const nom = window.prompt('Nom de la pièce (ex : Salon, Chambre 1) ?');
    if (!nom?.trim()) return;
    const newPieces = [...(fiche.pieces || []), {
      piece_id: `PCE-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      nom: nom.trim(),
      photos: [],
    }];
    try {
      await api.put(`/fiches-visite/${fiche.fiche_id}`, { ...fiche, pieces: newPieces });
      toast.success('Pièce ajoutée');
      await reload();
    } catch (e) {
      toast.error('Échec');
    }
  };

  const updatePieceEtat = async (piece_id, payload) => {
    try {
      await api.put(`/fiches-visite/${fiche.fiche_id}/pieces/${piece_id}/etat`, payload);
      await reload();
    } catch (e) {
      toast.error('Échec mise à jour');
    }
  };

  const removePiece = async (piece_id) => {
    if (!window.confirm('Supprimer cette pièce et toutes ses photos ?')) return;
    const newPieces = (fiche.pieces || []).filter(p => p.piece_id !== piece_id);
    try {
      await api.put(`/fiches-visite/${fiche.fiche_id}`, { ...fiche, pieces: newPieces });
      await reload();
    } catch (e) { toast.error('Échec'); }
  };

  const uploadPhoto = async (piece_id, file) => {
    // Récupère la position GPS du navigateur (permission utilisateur)
    const getGPS = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        pos => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });
    toast.loading('Localisation en cours…', { id: 'gps' });
    const gps = await getGPS();
    toast.dismiss('gps');
    if (!gps) toast.warning('GPS indisponible — photo enregistrée sans coordonnées');

    const fd = new FormData();
    fd.append('file', file);
    if (piece_id) fd.append('piece_id', piece_id);
    if (gps) {
      fd.append('lat', String(gps.lat));
      fd.append('lng', String(gps.lng));
      fd.append('accuracy', String(gps.accuracy));
    }
    fd.append('captured_at', new Date().toISOString());
    try {
      await api.post(`/fiches-visite/${fiche.fiche_id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo ajoutée');
      await reload();
    } catch (e) {
      toast.error('Échec upload');
    }
  };

  const removePhoto = async (piece_id, photo_index) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    const pieces = (fiche.pieces || []).map(p => {
      if (p.piece_id !== piece_id) return p;
      const photos = [...(p.photos || [])];
      photos.splice(photo_index, 1);
      return { ...p, photos };
    });
    try {
      await api.put(`/fiches-visite/${fiche.fiche_id}`, { ...fiche, pieces });
      await reload();
    } catch (e) { toast.error('Échec'); }
  };

  const saveSignature = async (dataUrl, nom, qualite) => {
    try {
      await api.post(`/fiches-visite/${fiche.fiche_id}/signature-client`, {
        signature_dataurl: dataUrl, nom, qualite,
      });
      toast.success('Signature enregistrée');
      setShowSignaturePad(false);
      await reload();
    } catch (e) {
      toast.error('Échec enregistrement signature');
    }
  };

  const clearSignature = async () => {
    if (!window.confirm('Retirer la signature du client ?')) return;
    try {
      await api.delete(`/fiches-visite/${fiche.fiche_id}/signature-client`);
      await reload();
    } catch (e) { toast.error('Échec'); }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/fiches-visite/${fiche.fiche_id}/etat-lieux/pdf`, {}, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etat-des-lieux-${(fiche.client_nom || 'chantier').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF généré');
    } catch (e) {
      toast.error('Échec génération PDF');
    }
    setGenerating(false);
  };

  // ============= UI =============

  if (creating || !fiche) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="etat-lieux-create-form">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onExit} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nouvel état des lieux</h1>
            <p className="text-sm text-slate-500">Informations de départ — remplissez le reste sur place.</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Client / Maître d'ouvrage *"><Input value={meta.client_nom} onChange={e => setMeta({ ...meta, client_nom: e.target.value })} data-testid="edl-client" /></Field>
            <Field label="Adresse du chantier *"><Input value={meta.adresse} onChange={e => setMeta({ ...meta, adresse: e.target.value })} data-testid="edl-adresse" /></Field>
            <Field label="Contact sur place"><Input value={meta.contact_sur_place} onChange={e => setMeta({ ...meta, contact_sur_place: e.target.value })} /></Field>
            <Field label="Téléphone contact"><Input value={meta.telephone_contact} onChange={e => setMeta({ ...meta, telephone_contact: e.target.value })} /></Field>
            <Field label="Date"><Input type="date" value={meta.date_visite} onChange={e => setMeta({ ...meta, date_visite: e.target.value })} /></Field>
            <Field label="Heure"><Input type="time" value={meta.heure_visite} onChange={e => setMeta({ ...meta, heure_visite: e.target.value })} /></Field>
            <Field label="Intervenant Reno Resolve"><Input value={meta.intervenant_reno} onChange={e => setMeta({ ...meta, intervenant_reno: e.target.value })} placeholder="Nom / Prénom" data-testid="edl-intervenant" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onExit}>Annuler</Button>
            <Button onClick={createFiche} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edl-create-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Créer et démarrer l'état des lieux
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasSig = !!fiche.signature_client_dataurl;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-24" data-testid="etat-lieux-edit-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onExit} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">État des lieux — {fiche.client_nom}</h1>
          <p className="text-sm text-slate-500">{fiche.adresse} · {fiche.date_visite} {fiche.heure_visite}</p>
        </div>
        <Button onClick={generatePDF} disabled={generating} className="bg-[#1C49A6] hover:bg-[#163C89] text-white" data-testid="edl-pdf-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
          Générer le PDF
        </Button>
      </div>

      {/* Bloc pièces */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Home className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Pièces & constats <span className="text-slate-400 font-normal">({(fiche.pieces || []).length})</span></h3>
          </div>
          <Button size="sm" onClick={addPiece} className="bg-slate-900 hover:bg-slate-800" data-testid="edl-add-piece">
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter une pièce
          </Button>
        </div>
        {(!fiche.pieces || fiche.pieces.length === 0) ? (
          <div className="p-8 text-center text-slate-400 text-sm">Aucune pièce. Ajoutez la première pièce pour commencer.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {fiche.pieces.map(p => (
              <PieceEditor
                key={p.piece_id}
                piece={p}
                onEtatChange={(payload) => updatePieceEtat(p.piece_id, payload)}
                onUploadPhoto={(file) => uploadPhoto(p.piece_id, file)}
                onRemovePhoto={(idx) => removePhoto(p.piece_id, idx)}
                onRemove={() => removePiece(p.piece_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Compteurs & réserves */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
          <ClipboardList className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Compteurs & réserves globales</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Compteur eau"><Input value={meta.releve_compteur_eau} onChange={e => setMeta({ ...meta, releve_compteur_eau: e.target.value })} placeholder="ex : 03251 m³" /></Field>
          <Field label="Compteur élec"><Input value={meta.releve_compteur_elec} onChange={e => setMeta({ ...meta, releve_compteur_elec: e.target.value })} placeholder="ex : 45872 kWh" /></Field>
          <Field label="Compteur gaz"><Input value={meta.releve_compteur_gaz} onChange={e => setMeta({ ...meta, releve_compteur_gaz: e.target.value })} placeholder="ex : 8420 m³" /></Field>
        </div>
        <Field label="Réserves & points de vigilance (constat global)">
          <Textarea rows={4} value={meta.reserves_globales} onChange={e => setMeta({ ...meta, reserves_globales: e.target.value })} placeholder="Points préexistants à conserver au dossier avant démarrage…" data-testid="edl-reserves" />
        </Field>
        <div className="flex justify-end">
          <Button onClick={saveMeta} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edl-save-meta">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Signature client */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Signature du client (sur place)</h3>
          </div>
          {hasSig && (
            <button onClick={clearSignature} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Retirer
            </button>
          )}
        </div>
        {hasSig ? (
          <div className="flex items-center gap-6">
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <img src={fiche.signature_client_dataurl} alt="Signature" style={{ maxWidth: 320, maxHeight: 120 }} />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-slate-900">{fiche.signature_client_nom || '—'}</p>
              <p className="text-slate-500">{fiche.signature_client_qualite || ''}</p>
              <p className="text-xs text-slate-400 mt-1">Signé le {fiche.signature_date ? new Date(fiche.signature_date).toLocaleString('fr-FR') : '—'}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-slate-500 mb-3">Aucune signature enregistrée. Faites signer le client directement sur l'écran.</p>
            <Button onClick={() => setShowSignaturePad(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edl-open-signature">
              <PenLine className="w-4 h-4 mr-2" /> Ouvrir le pad de signature
            </Button>
          </div>
        )}
      </div>

      {showSignaturePad && (
        <SignaturePadModal
          onCancel={() => setShowSignaturePad(false)}
          onSave={saveSignature}
        />
      )}
    </div>
  );
}


// ============================================================================
// Piece editor
// ============================================================================
function PieceEditor({ piece, onEtatChange, onUploadPhoto, onRemovePhoto, onRemove }) {
  const [local, setLocal] = useState({
    etat_sols: piece.etat_sols || '',
    etat_murs: piece.etat_murs || '',
    etat_plafond: piece.etat_plafond || '',
    etat_menuiseries: piece.etat_menuiseries || '',
    etat_reseaux: piece.etat_reseaux || '',
    observations: piece.observations || '',
  });
  const [expanded, setExpanded] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    setLocal({
      etat_sols: piece.etat_sols || '',
      etat_murs: piece.etat_murs || '',
      etat_plafond: piece.etat_plafond || '',
      etat_menuiseries: piece.etat_menuiseries || '',
      etat_reseaux: piece.etat_reseaux || '',
      observations: piece.observations || '',
    });
  }, [piece]);

  const commit = () => onEtatChange(local);

  return (
    <div className="p-5 space-y-3" data-testid={`edl-piece-${piece.piece_id}`}>
      <div className="flex items-center justify-between">
        <button onClick={() => setExpanded(!expanded)} className="text-left">
          <h4 className="font-semibold text-slate-900">{piece.nom}</h4>
          <p className="text-xs text-slate-500">{(piece.photos || []).length} photo(s)</p>
        </button>
        <button onClick={onRemove} className="p-1.5 text-slate-300 hover:text-red-500" data-testid={`edl-remove-piece-${piece.piece_id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { k: 'etat_sols',        l: 'État sols' },
              { k: 'etat_murs',        l: 'État murs' },
              { k: 'etat_plafond',     l: 'État plafond' },
              { k: 'etat_menuiseries', l: 'État menuiseries' },
              { k: 'etat_reseaux',     l: 'État réseaux visibles' },
            ].map(({ k, l }) => (
              <Field key={k} label={l}>
                <select
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  value={local[k]}
                  onChange={e => setLocal({ ...local, [k]: e.target.value })}
                  onBlur={commit}
                  data-testid={`edl-${piece.piece_id}-${k}`}
                >
                  {ETAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
            ))}
          </div>
          <Field label="Observations & précisions">
            <Textarea rows={2} value={local.observations} onChange={e => setLocal({ ...local, observations: e.target.value })} onBlur={commit}
              placeholder="Ex : fissure existante au mur nord, trace d'humidité au plafond…" />
          </Field>

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Photos horodatées & géolocalisées</label>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onUploadPhoto(f); e.target.value = ''; }}
                data-testid={`edl-photo-input-${piece.piece_id}`} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`edl-photo-btn-${piece.piece_id}`}>
                <Camera className="w-4 h-4 mr-1.5" /> Prendre une photo
              </Button>
            </div>
            {(piece.photos || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucune photo pour cette pièce.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {piece.photos.map((ph, i) => (
                  <div key={i} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                    <img src={ph.url} alt="" className="w-full h-32 object-cover" />
                    <button onClick={() => onRemovePhoto(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100">
                      <X className="w-3 h-3 text-red-600" />
                    </button>
                    <div className="p-2 text-[10px] text-slate-600 space-y-0.5">
                      <p className="flex items-center gap-1"><Clock className="w-3 h-3" />{ph.captured_at ? new Date(ph.captured_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {ph.lat != null ? `${ph.lat.toFixed(4)}, ${ph.lng.toFixed(4)}` : 'GPS off'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================================
// Signature pad modal
// ============================================================================
function SignaturePadModal({ onCancel, onSave }) {
  const canvasRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [nom, setNom] = useState('');
  const [qualite, setQualite] = useState('');
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2;
    c.height = rect.height * 2;
    const ctx = c.getContext('2d');
    ctx.scale(2, 2);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = getPos(e); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(x, y); setEmpty(false); };
  const move  = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = getPos(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(x, y); ctx.stroke(); };
  const stop  = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext('2d'); ctx.clearRect(0, 0, c.width, c.height); setEmpty(true); };
  const save = () => {
    if (empty) { toast.error('Signature vide'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl, nom.trim(), qualite.trim());
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="signature-pad-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Signature du client</h3>
          <button onClick={onCancel}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom & prénom"><Input value={nom} onChange={e => setNom(e.target.value)} data-testid="sig-nom" /></Field>
            <Field label="Qualité"><Input value={qualite} onChange={e => setQualite(e.target.value)} placeholder="Maire, DGS, propriétaire…" /></Field>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Signature (dessinez ci-dessous)</label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
              <canvas
                ref={canvasRef}
                onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
                onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
                style={{ width: '100%', height: 220, touchAction: 'none' }}
                data-testid="signature-canvas"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={clear}><Eraser className="w-4 h-4 mr-1.5" /> Effacer</Button>
            <Button variant="outline" onClick={onCancel}>Annuler</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sig-save">
              <Check className="w-4 h-4 mr-1.5" /> Valider la signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
